// PlanRevealScreen.jsx
// Day-1 plan reveal — now fully data-driven off GET /api/plan.
//
// On mount, fetch every course + its extracted events, compute a week grid
// spanning min(event_date) → max(event_date), and place each event in its
// actual cell. A pill row at the top toggles which courses are shown.
//
// ingestResult (optional) from a fresh ingest bumps that course into the
// visible set and previews its first 10 dates while the full plan loads.

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { CoursePills, TINT } from '../components/CoursePills';

const tintOf = (t) => TINT[t] || TINT.amber;

const EVENT_TYPE_LABEL = {
  midterm: "Midterm",
  final:   "Final",
  pset:    "PSET",
  lecture: "Lecture",
  holiday: "Holiday",
  other:   "Event",
};

const MS_DAY = 86400000;

function parseISO(s) {
  if (!s || typeof s !== "string" || s.length < 10) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}

function fmtMonthDay(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function mondayOf(d) {
  const day = d.getDay(); // 0 = Sun
  const offset = (day + 6) % 7; // days since Monday
  const m = new Date(d);
  m.setDate(m.getDate() - offset);
  m.setHours(0, 0, 0, 0);
  return m;
}

function diffDays(a, b) {
  return Math.floor((b.getTime() - a.getTime()) / MS_DAY);
}

export function PlanRevealScreen({ ingestResult }) {
  const navigate = useNavigate();
  const location = useLocation();
  const focusCourseId = location.state?.focusCourseId;
  const onBegin = (presetId) => navigate('/session', { state: { presetId, focusCourseId } });
  const onBack = () => navigate('/');
  const [plan, setPlan] = React.useState(null);
  const [selected, setSelected] = React.useState(() => new Set());
  const [err, setErr] = React.useState("");
  const [openCellKey, setOpenCellKey] = React.useState(null);
  const popoverRef = React.useRef(null);

  // Close popover when clicking outside.
  React.useEffect(() => {
    if (!openCellKey) return;
    function handleClick(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpenCellKey(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openCellKey]);

  // When an ingest just happened, poll /api/ingest/status until the
  // background decomposition finishes, then refetch the plan so the
  // anchor_count + side effects (graph nodes, etc.) show up.
  const [decompStatus, setDecompStatus] = React.useState(
    ingestResult?.decomposition === "queued" ? "running" : null
  );
  React.useEffect(() => {
    const cid = ingestResult?.course;
    if (!cid || ingestResult?.decomposition !== "queued") return;
    let alive = true;
    setDecompStatus("running");
    const tick = async () => {
      if (!alive) return;
      try {
        const s = await api.getIngestStatus(cid);
        if (!alive) return;
        if (s.state === "done" || s.state === "error") {
          setDecompStatus(s.state);
          // refetch plan to pick up new anchors + updated course meta
          const p = await api.getPlan();
          if (!alive) return;
          setPlan(p);
          return;
        }
      } catch (e) {
        // swallow — we'll retry
      }
      setTimeout(tick, 4000);
    };
    setTimeout(tick, 4000);
    return () => { alive = false; };
  }, [ingestResult?.course, ingestResult?.decomposition]);

  // Fetch the full plan on mount / after a fresh ingest.
  React.useEffect(() => {
    let alive = true;
    setErr("");
    api.getPlan()
      .then(p => {
        if (!alive) return;
        setPlan(p);
        // Default selection rule (in priority order):
        // 1. focusCourseId set via Home → course card → show just that one.
        // 2. Fresh ingest → show just that course.
        // 3. Otherwise → show everything.
        let defaultSel;
        if (focusCourseId && p.courses.some(c => c.id === focusCourseId)) {
          defaultSel = new Set([focusCourseId]);
        } else if (ingestResult?.course && p.courses.some(c => c.id === ingestResult.course)) {
          defaultSel = new Set([ingestResult.course]);
        } else {
          defaultSel = new Set(p.courses.map(c => c.id));
        }
        setSelected(defaultSel);
      })
      .catch(e => { if (alive) setErr("Couldn't load the plan. Refresh to retry."); });
    return () => { alive = false; };
  }, [ingestResult?.course, focusCourseId]);

  const courses = plan?.courses || [];
  const allEvents = plan?.events || [];
  // Window: anchor at Monday of (today − 1 week), span up to 16 weeks forward.
  // Events outside this range are filtered out entirely, so stacking courses
  // from different years doesn't blow up the grid into a 3-year strip.
  const MAX_WEEKS = 16;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const gridStart = mondayOf(new Date(today.getTime() - 7 * MS_DAY));
  const maxGridEnd = new Date(gridStart.getTime() + (MAX_WEEKS * 7 - 1) * MS_DAY);

  const events = allEvents.filter(e => {
    if (!selected.has(e.course_id)) return false;
    const d = parseISO(e.date);
    return d && d >= gridStart && d <= maxGridEnd;
  });

  // Trim trailing empty weeks, but keep at least 4 weeks visible.
  const isoDates = events.map(e => parseISO(e.date)).filter(Boolean);
  const latest = isoDates.length
    ? new Date(Math.max(...isoDates.map(d => d.getTime())))
    : new Date(today.getTime() + 27 * MS_DAY);
  const gridEnd = mondayOf(latest);
  gridEnd.setDate(gridEnd.getDate() + 6);
  let weeks = Math.max(4, Math.ceil((diffDays(gridStart, gridEnd) + 1) / 7));
  weeks = Math.min(weeks, MAX_WEEKS);

  // Bucket events by grid cell (row, col) for quick lookup.
  const cellKey = (d) => {
    const deltaDays = diffDays(gridStart, d);
    const r = Math.floor(deltaDays / 7);
    const c = deltaDays % 7;
    return r >= 0 && r < weeks && c >= 0 ? `${r}-${c}` : null;
  };
  const eventsByCell = {};
  events.forEach(e => {
    const d = parseISO(e.date);
    if (!d) return;
    const k = cellKey(d);
    if (!k) return;
    (eventsByCell[k] = eventsByCell[k] || []).push(e);
  });

  const courseById = Object.fromEntries(courses.map(c => [c.id, c]));

  const firstSession = events
    .filter(e => {
      const d = parseISO(e.date);
      return d && d >= today;
    })
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];

  const exam = events
    .filter(e => e.type === "final" || e.type === "midterm")
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];

  const toggleCourse = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === 0) { next.add(id); } // never leave zero courses selected
      return next;
    });
  };

  const daysOut = exam ? Math.max(1, diffDays(today, parseISO(exam.date))) : 42;

  return (
    <section className="planreveal is-revealed">
      <div className="pr-top">
        <button className="back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 6l-6 6 6 6"/></svg>
          Back
        </button>
        <div className="pr-chip-row">
          <span className="pr-chip">Plan v1 · editable</span>
          {decompStatus === "running" && (
            <span className="pr-chip pr-chip-live">Building concept map…</span>
          )}
          {decompStatus === "done" && (
            <span className="pr-chip pr-chip-ok">Concept map ready</span>
          )}
          {decompStatus === "error" && (
            <span className="pr-chip pr-chip-err">Map build failed</span>
          )}
        </div>
      </div>

      <header className="pr-hero">
        <div className="pr-eyebrow">Your plan</div>
        <h1 className="pr-serif">
          {daysOut} days. <span className="pr-dim">One derivation at a time.</span>
        </h1>
        <p className="pr-sub">
          {courses.length > 0
            ? "You told us your goal, we mapped what you know, and this is the shortest honest path there. Edit anything."
            : "Import a syllabus from the Study tab to generate your plan. The calendar will fill in as we pull dates out of your materials."}
        </p>
      </header>

      {courses.length > 0 && (
        <CoursePills courses={courses} selected={selected} onToggle={toggleCourse} />
      )}

      {err && <div className="pr-err">{err}</div>}

      {exam && (
        <div className="pr-goalbar">
          <div className="pr-goalbar-left">
            <div className="pr-goalbar-eyebrow">Today</div>
            <div className="pr-goalbar-val">{fmtMonthDay(today)}</div>
          </div>
          <div className="pr-goalbar-track">
            <div className="pr-goalbar-fill" style={{width: (Math.min(1, 1/daysOut) * 100) + "%"}}/>
            <div className="pr-goalbar-pin pr-goalbar-pin-today" style={{left: "0%"}}>
              <div className="pr-goalbar-pin-dot"/>
            </div>
            <div className="pr-goalbar-pin pr-goalbar-pin-exam" style={{left: "100%"}}>
              <div className="pr-goalbar-pin-dot pr-exam-dot"/>
            </div>
          </div>
          <div className="pr-goalbar-right">
            <div className="pr-goalbar-eyebrow">
              {(courseById[exam.course_id]?.name || "Course") + " " + (EVENT_TYPE_LABEL[exam.type] || "milestone").toLowerCase()}
            </div>
            <div className="pr-goalbar-val">{fmtMonthDay(parseISO(exam.date))}</div>
          </div>
        </div>
      )}

      <div className="pr-body">
        <div className="pr-calendar">
          <div className="pr-cal-head">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
              <div key={d} className="pr-cal-day">{d}</div>
            ))}
          </div>
          <div className="pr-cal-grid">
            {Array.from({length: weeks}).map((_, r) => (
              <React.Fragment key={r}>
                {Array.from({length: 7}).map((_, c) => {
                  const cellDate = new Date(gridStart.getTime() + (r * 7 + c) * MS_DAY);
                  const isToday_ = cellDate.getTime() === today.getTime();
                  const items = eventsByCell[`${r}-${c}`] || [];
                  // Derive a cell-level accent from the most important event:
                  // final > midterm > pset > holiday. Lectures get no cell fill.
                  const priority = { final: 4, midterm: 3, pset: 2, holiday: 1 };
                  const accent = items.reduce((best, ev) => {
                    const p = priority[ev.type] || 0;
                    return p > (best.p || 0) ? { p, type: ev.type } : best;
                  }, {}).type;
                  return (
                    <div
                      key={c}
                      className={
                        "pr-cal-cell" +
                        (isToday_ ? " pr-today" : "") +
                        (accent ? " pr-cal-cell-" + accent : "")
                      }
                      style={{position: "relative"}}
                    >
                      <div className="pr-cal-date">
                        <span>{cellDate.getDate()}</span>
                        {isToday_ && <span className="pr-cal-todaydot"/>}
                      </div>
                      {items.slice(0, 3).map(e => {
                        const t = tintOf(courseById[e.course_id]?.tint);
                        return (
                          <div
                            key={e.id}
                            className={"pr-event pr-event-" + e.type}
                            style={{ background: t.bg, color: t.fg, borderColor: t.border }}
                            title={(courseById[e.course_id]?.name || "") + " · " + e.label}
                          >
                            <div className="pr-event-label">{EVENT_TYPE_LABEL[e.type] || "Event"}</div>
                            <div className="pr-event-sub">{e.label.length > 22 ? e.label.slice(0, 20) + "…" : e.label}</div>
                          </div>
                        );
                      })}
                      {items.length >= 4 && (() => {
                        const ck = `${r}-${c}`;
                        const isOpen = openCellKey === ck;
                        return (
                          <>
                            <button
                              className="pr-event-more pr-event-more-btn"
                              onClick={e => { e.stopPropagation(); setOpenCellKey(isOpen ? null : ck); }}
                            >
                              +{items.length - 3} more
                            </button>
                            {isOpen && (
                              <div className="pr-event-pop" ref={popoverRef}>
                                <button className="pr-event-pop-close" onClick={() => setOpenCellKey(null)}>✕</button>
                                {items.map(ev => {
                                  const t = tintOf(courseById[ev.course_id]?.tint);
                                  return (
                                    <div key={ev.id} className="pr-event-pop-item"
                                      style={{ borderLeftColor: t.border }}>
                                      <div style={{fontWeight:700,fontSize:11}}>{EVENT_TYPE_LABEL[ev.type] || "Event"}</div>
                                      <div style={{fontSize:12,color:"var(--ink-2)"}}>{ev.label}</div>
                                      <div style={{fontSize:10,color:"var(--ink-3)"}}>{courseById[ev.course_id]?.name || ""}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        <aside className="pr-side">
          <div className="pr-card pr-card-next">
            <div className="pr-eyebrow">{firstSession ? "Next up" : "When you're ready"}</div>
            <div className="pr-next-serif">
              {firstSession ? firstSession.label : "Import a class to start your plan."}
            </div>
            <div className="pr-next-body">
              {firstSession
                ? `${fmtMonthDay(parseISO(firstSession.date))} · ${courseById[firstSession.course_id]?.name || ""}`
                : "The first session will appear here once a syllabus is parsed."}
            </div>
            <button
              className="btn-primary"
              onClick={() => onBegin(selected.has("phys70") ? "phys70_pset1" : null)}
              disabled={!firstSession}
            >Begin first session →</button>
            {/* Demo convenience: when phys70 is loaded, expose the pset PDF so
                the user can open it alongside the session view. */}
            {courses.some(c => c.id === "phys70") && (
              <a
                href="/demo/phys70_pset1.pdf"
                target="_blank"
                rel="noopener"
                className="btn-ghost"
                style={{ marginTop: 6, textAlign: "center", textDecoration: "none" }}
              >
                Open PSET 1 (PDF) ↗
              </a>
            )}
          </div>

          <div className="pr-card">
            <div className="pr-eyebrow">What we're building toward</div>
            <ul className="pr-goals">
              {courses.filter(c => selected.has(c.id)).map(c => (
                <li key={c.id}>
                  <span className="pr-goal-dot" style={{ background: tintOf(c.tint).border }}/> {c.name} · {c.anchor_count} anchors
                </li>
              ))}
              {exam && (
                <li><span className="pr-goal-dot pr-goal-exam"/> {EVENT_TYPE_LABEL[exam.type] || "Exam"} · {fmtMonthDay(parseISO(exam.date))}</li>
              )}
            </ul>
          </div>

          <div className="pr-card pr-card-summary">
            <div className="pr-summary-row">
              <div>
                <div className="pr-summary-n">{events.length}</div>
                <div className="pr-summary-l">events</div>
              </div>
              <div>
                <div className="pr-summary-n">{courses.filter(c => selected.has(c.id)).length}</div>
                <div className="pr-summary-l">courses</div>
              </div>
              <div>
                <div className="pr-summary-n">{daysOut}</div>
                <div className="pr-summary-l">days</div>
              </div>
            </div>
            <div className="pr-summary-note">
              Data-driven. Everything here comes from what you uploaded; reimport to refine.
            </div>
          </div>
        </aside>
      </div>

      <footer className="pr-foot">
        <div className="pr-foot-l">
          <span className="pr-foot-lock">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>
            Only you see this plan.
          </span>
        </div>
        <div className="pr-foot-r">
          <button className="btn-ghost">Edit schedule</button>
          <button
            className="btn-primary"
            onClick={() => onBegin(selected.has("phys70") ? "phys70_pset1" : null)}
            disabled={!firstSession}
          >Begin first session</button>
        </div>
      </footer>
    </section>
  );
}

